import { useQuery } from '@tanstack/react-query';
import { setCredentials } from 'app/services/twilio/credentials';
import ISIConfig from 'app/copied/models/ISIConfig';
import { ChatCustomization } from 'app/copied/types/ChatItems';
import { ChatItemType } from 'app/services/chatBot';

import fetchLiveChat from '../../services/liveChat';
import useFetchHelpCenter from './useFetchHelpCenter';

interface AdditionalInfoItem {
  anchor: string;
  content: string;
}

interface LiveChatConfig {
  autoopen_chat: boolean;
  autoopen_chat_rule: string;
  autoopen_mobile_disabled: boolean;
  title: string;
  use_toc_pp_checkbox: boolean;
  toc_pp_text: string;
  additional_info: AdditionalInfoItem[];
  landing_button_title: string;
  working_hours: string;
  working_days: string;
  timezone_name: string;
  text_under_working_hours: string;
  agent_status_header_text: string;
  customization: ChatCustomization;
  isi_block: ISIConfig;
  phone_call_cta_enabled: boolean;
  phone_call_cta_text: string;
  phone_number: string;
}

interface Params {
  placeholder: string;
  reporting_tags: string[];
  label?: string;
  additional_info?: AdditionalInfoItem[];
}

interface Item {
  item_type: string;
  ref_id: string;
  required: boolean;
  params: Params;
  value: string | boolean | null;
  is_valid: boolean | null;
  errors: string[] | null;
}

interface FormParameters {
  submit_text?: string;
}

export interface LiveChatItem {
  type: ChatItemType;
  ref_id?: string;
  form_type?: string;
  items?: Item[];
  form_parameters?: FormParameters;
  dismissable?: boolean;
  dismissed?: boolean;
  completed?: boolean;
  blocking?: boolean;
  event_dt: string;
  message?: string;
  additional_info?: AdditionalInfoItem[];
  identity?: string;
  source?: string;
  delay_duration?: number | null;
  conversation_sid?: string;
  chat_sid?: string;
  user_token?: string;
  static_ref_id?: string;
}

export interface TwilioCredentials {
  conversation_sid: string;
  chat_sid: string;
  user_token: string;
}

export interface LiveChatApiResponse {
  uuid: string;
  theme: object;
  config: LiveChatConfig;
  chat_items: LiveChatItem[];
  twilio_credentials: TwilioCredentials;
  nurses_are_online: boolean;
  next_online_status_change_dt: string;
  test_only: boolean;
}

export interface LiveChatQueryResponse extends LiveChatApiResponse {
  consentMessage?: LiveChatItem;
}

export default function useFetchLiveChat() {
  const { data: helpCenter } = useFetchHelpCenter();
  const uuid = helpCenter?.uuid;

  return useQuery<LiveChatQueryResponse>({
    queryKey: ['liveChat', uuid],
    queryFn: () => fetchLiveChat({ uuid }),

    enabled: !!uuid,
    select: (data: LiveChatQueryResponse) => {
      if (data.twilio_credentials) {
        setCredentials(data.twilio_credentials);
      }

      return {
        ...data,
        chat_items: data.chat_items.filter((item) => item.ref_id !== 'terms&conditions' || item.completed).reverse(),
        consentMessage: data.chat_items.find((item) => item.ref_id === 'terms&conditions'),
      };
    },
  });
}
